import { useRef } from 'react';

/**
 * A controller for delaying the unmounting of the share dialog controller's
 * root until its last dialog displayed has completed its exit transition.
 */
export type TeardownController = {
	/**
	 * Inform the teardown controller that a dialog that is among those that
	 * could be the last to be displayed in a workflow, has completed its exit
	 * transition.
	 */
	potentialFinalDialogDidCompleteClose: () => void;
	/**
	 * Inform the teardown controller that the teardown should begin as soon as
	 * the next `potentialFinalDialogDidCompleteClose` function is called.
	 */
	tearDownAfterPotentialFinalDialogCompletesClose: () => void;
};

/**
 * A hook for delaying the unmounting of the share dialog controller's root
 * until its last dialog displayed has completed its exit transition.
 *
 * Not controlling this flow would mean that the `<ModalTransition>` host would
 * get unmounted, causing an instantaneous dialog disappearance.
 */
export function useTeardownController(teardown: () => void): TeardownController {
	const shouldTearDownOnNextClose = useRef(false);
	return {
		potentialFinalDialogDidCompleteClose() {
			if (shouldTearDownOnNextClose.current) teardown();
		},
		tearDownAfterPotentialFinalDialogCompletesClose() {
			shouldTearDownOnNextClose.current = true;
		},
	};
}
